import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatIndentIncreaseRoundedIcon from "@mui/icons-material/FormatIndentIncreaseRounded";
import FormatIndentDecreaseRoundedIcon from "@mui/icons-material/FormatIndentDecreaseRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import Overlay from "components/Overlay/Overlay";
import Autocomplete from "@mui/material/Autocomplete";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

// All service arguments that can drive template auto-selection.
// hasList: the Value is picked from a list (Match Type is hidden, always
// exact). Typed fields show the Match Type (exact / contains).
const RULE_FIELD_OPTIONS = [
  { key: "Client", label: "Client", hasList: true },
  { key: "Requestor", label: "Requestor", hasList: false },
  { key: "Linguist", label: "Linguist", hasList: true },
  { key: "InterpretationType", label: "Interpretation Type", hasList: true },
  { key: "Language", label: "Language", hasList: true },
  { key: "Mode", label: "Mode", hasList: true },
  { key: "ServiceCategory", label: "Service Category", hasList: true },
  { key: "InterpretationStatus", label: "Interpretation Status", hasList: true },
  { key: "Name", label: "Name", hasList: false },
  { key: "Details", label: "Details", hasList: false },
  { key: "Address", label: "Address", hasList: false },
  { key: "ProjectNumber", label: "Project Number", hasList: false },
];

const MODE_OPTIONS = ["Consecutive", "Simultaneous"];

const SERVICE_CATEGORY_OPTIONS = [
  "Interpretation",
  "Translation",
  "Other Service",
];

const extractPlaceholders = (html) => {
  if (!html) return [];
  const matches = html.match(/\{[^}]+\}/g) || [];
  const unique = Array.from(new Set(matches.map((m) => m.trim()))).filter(
    Boolean,
  );
  return unique;
};

const CommunicationTemplateNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const editorRef = useRef(null);
  const subjectRef = useRef(null);

  const isEditMode = !!overlayData?.template?.id;

  // Edit lock for existing communication templates only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "communicationTemplate",
    isEditMode ? overlayData?.template?.id : null,
  );

  const [name, setName] = useState(overlayData?.template?.Name || "");
  const [description, setDescription] = useState(
    overlayData?.template?.Description || "",
  );
  const [channel, setChannel] = useState(
    overlayData?.template?.Channel || "Email",
  );
  const [type, setType] = useState(overlayData?.template?.Type || "Template");
  const [subject, setSubject] = useState(overlayData?.template?.Subject || "");
  const [bodyHtml, setBodyHtml] = useState(overlayData?.template?.Body || "");
  const [saving, setSaving] = useState(false);
  const [lastFocused, setLastFocused] = useState("body");
  const [linkUrl, setLinkUrl] = useState("");
  const [savedSelection, setSavedSelection] = useState(null);

  // Auto-select rules (TemplateMatchRule) for this template.
  const [matchRules, setMatchRules] = useState(() =>
    (overlayData?.template?.MatchRules || []).map((rule) => ({
      Field: rule.Field,
      MatchType: rule.MatchType,
      Value: rule.Value,
    })),
  );
  // Option lists for rule values, fetched from the live data so values
  // match the actual subtypes / clients / languages exactly.
  const [subtypeOptions, setSubtypeOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [linguistOptions, setLinguistOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      api.get("/services/interpretations/types/options"),
      api.get("/clients/brief"),
      api.get("/languages"),
      api.get("/linguists/brief"),
      api.get("/services/interpretations/statuses"),
    ])
      .then(
        ([
          subtypesRes,
          clientsRes,
          languagesRes,
          linguistsRes,
          statusesRes,
        ]) => {
          if (!isMounted) return;
          setSubtypeOptions(
            Array.isArray(subtypesRes.data) ? subtypesRes.data : [],
          );
          setClientOptions(
            Array.isArray(clientsRes.data) ? clientsRes.data : [],
          );
          setLanguageOptions(
            Array.isArray(languagesRes.data) ? languagesRes.data : [],
          );
          setLinguistOptions(
            Array.isArray(linguistsRes.data) ? linguistsRes.data : [],
          );
          setStatusOptions(
            Array.isArray(statusesRes.data) ? statusesRes.data : [],
          );
        },
      )
      .catch((error) => {
        console.error("Error fetching template rule options:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const addMatchRule = () => {
    setMatchRules((prev) => [
      ...prev,
      { Field: "InterpretationType", MatchType: "exact", Value: "" },
    ]);
  };

  const removeMatchRule = (index) => {
    setMatchRules((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMatchRule = (index, key, value) => {
    setMatchRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, [key]: value } : rule)),
    );
  };

  // Detect duplicate rules: same Field + same non-empty Value. The set holds
  // the indexes of the offending rules (all rows sharing a duplicate pair).
  const duplicateRuleIndexes = useMemo(() => {
    const seen = new Map();
    const duplicates = new Set();

    matchRules.forEach((rule, index) => {
      const field = String(rule.Field || "").trim();
      const value = String(rule.Value || "").trim();
      if (!field || !value) return;

      const key = `${field}::${value.toLowerCase()}`;
      if (seen.has(key)) {
        duplicates.add(seen.get(key));
        duplicates.add(index);
      } else {
        seen.set(key, index);
      }
    });

    return duplicates;
  }, [matchRules]);

  const hasDuplicateRules = duplicateRuleIndexes.size > 0;

  // Value input for a rule row: constrained selects/autocompletes for
  // option-backed fields, free text for typed fields (Name, Details, etc.).
  const renderRuleValueInput = (rule, index, isDuplicate = false) => {
    const commonProps = {
      size: "small",
      fullWidth: true,
      label: "Value",
      error: isDuplicate,
      helperText: isDuplicate
        ? "The value of this field matches another"
        : undefined,
    };

    // Typed fields (no list): free text, Match Type visible.
    const typedFieldConfig = {
      Name: { placeholder: "e.g. Home Visit" },
      Details: { placeholder: "e.g. Medical" },
      Address: { placeholder: "e.g. 123 Main St" },
      Requestor: { placeholder: "e.g. John Doe" },
      ProjectNumber: { placeholder: "e.g. 26081001" },
    };

    const typedConfig = typedFieldConfig[rule.Field];
    if (typedConfig) {
      return (
        <TextField
          {...commonProps}
          placeholder={typedConfig.placeholder}
          value={rule.Value}
          onChange={(e) => updateMatchRule(index, "Value", e.target.value)}
        />
      );
    }

    if (rule.Field === "Mode") {
      return (
        <TextField
          {...commonProps}
          select
          value={rule.Value}
          onChange={(e) => updateMatchRule(index, "Value", e.target.value)}
        >
          {MODE_OPTIONS.map((mode) => (
            <MenuItem key={mode} value={mode}>
              {mode}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (rule.Field === "ServiceCategory") {
      return (
        <TextField
          {...commonProps}
          select
          value={rule.Value}
          onChange={(e) => updateMatchRule(index, "Value", e.target.value)}
        >
          {SERVICE_CATEGORY_OPTIONS.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    const options =
      rule.Field === "InterpretationType"
        ? subtypeOptions.map((s) => s.Name).filter(Boolean)
        : rule.Field === "Client"
          ? clientOptions.map((c) => c.Account).filter(Boolean)
          : rule.Field === "Linguist"
            ? linguistOptions.map((l) => l.Name).filter(Boolean)
            : rule.Field === "InterpretationStatus"
              ? statusOptions.filter(Boolean)
              : languageOptions.map((l) => l.Name).filter(Boolean);

    return (
      <Autocomplete
        freeSolo
        size="small"
        fullWidth
        options={options}
        value={rule.Value}
        onInputChange={(event, newValue) =>
          updateMatchRule(index, "Value", newValue || "")
        }
        renderInput={(params) => (
          <TextField {...params} {...commonProps} size="small" />
        )}
      />
    );
  };


  usePageTitle(
    isEditMode ? "Edit Communication Template" : "New Communication Template",
  );

  // Initialize editor content once on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = overlayData?.template?.Body || "";
    }
    setBodyHtml(overlayData?.template?.Body || "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeOverlay = () => setOverlayName("");

  const getPlainText = (html) => {
    if (!html) return "";
    return html
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleBodyInput = (e) => {
    setBodyHtml(e.currentTarget.innerHTML);
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertPlaceholderInBody = (placeholder) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();

    // Use document.execCommand for broad browser support
    const successful = document.execCommand("insertText", false, placeholder);

    if (!successful) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(placeholder));

      // Move cursor to end of inserted text
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    setBodyHtml(el.innerHTML);
  };

  const insertPlaceholderInSubject = (placeholder) => {
    const input = subjectRef.current;
    if (!input) return;
    const start = input.selectionStart ?? subject.length;
    const end = input.selectionEnd ?? start;
    const next = `${subject.slice(0, start)}${placeholder}${subject.slice(
      end,
    )}`;
    setSubject(next);

    // Restore caret after state update
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + placeholder.length;
      input.setSelectionRange(pos, pos);
    });
  };

  const insertPlaceholder = (placeholder) => {
    if (lastFocused === "subject") {
      insertPlaceholderInSubject(placeholder);
    } else {
      insertPlaceholderInBody(placeholder);
    }
  };

  const handleOpenLinkDialog = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0));
    }

    openDialog({
      theme: theme,
      title: "Insert Hyperlink",
      message: "",
      customContent: (
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="URL"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreateLink();
              }
            }}
            autoFocus
          />
        </Box>
      ),
      tertiaryAction: null,
      secondaryAction: "Cancel",
      primaryAction: "Insert Link",
      tertiaryOnClick: null,
      secondaryOnClick: () => {
        closeDialog();
        setLinkUrl("");
        setSavedSelection(null);
      },
      primaryOnClick: () => {
        handleCreateLink();
      },
    });
  };

  const handleCloseLinkDialog = () => {
    closeDialog();
    setLinkUrl("");
    setSavedSelection(null);
  };

  const handleCreateLink = () => {
    if (!linkUrl.trim()) {
      showSnackbar("Please enter a URL", theme, "error");
      return;
    }

    const el = editorRef.current;
    if (!el) return;

    el.focus();

    // Restore the saved selection
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
    }

    // Create link with the provided URL
    let url = linkUrl.trim();
    // Add protocol if not present
    if (!url.match(/^https?:\/\//)) {
      url = "https://" + url;
    }

    document.execCommand("createLink", false, url);
    setBodyHtml(el.innerHTML);
    handleCloseLinkDialog();
  };

  const handleSave = async () => {
    if (saving || isLocked) {
      return;
    }

    if (!name.trim()) {
      showSnackbar("Name is required", theme, "error");
      return;
    }
    if (type === "Template" && channel === "Email" && !subject.trim()) {
      showSnackbar("Subject is required for Email templates", theme, "error");
      return;
    }
    if (!getPlainText(bodyHtml)) {
      showSnackbar("Body is required", theme, "error");
      return;
    }

    const payload = {
      Name: name.trim(),
      Description: description.trim() || null,
      Channel: channel,
      Type: type,
      Body: bodyHtml,
    };

    // Auto-select rules: rows with a value are sent; the server replaces
    // the template's rules wholesale (empty array clears them).
    payload.MatchRules = matchRules
      .filter((rule) => String(rule.Value || "").trim().length > 0)
      .map((rule) => ({
        Field: rule.Field,
        MatchType: rule.MatchType,
        Value: String(rule.Value).trim(),
      }));

    if (type === "Template" && channel === "Email") {
      payload.Subject = subject.trim();
      payload.Placeholders = Array.from(
        new Set([
          ...extractPlaceholders(bodyHtml),
          ...extractPlaceholders(subject),
        ]),
      );
    } else if (type === "Template" && channel === "SMS") {
      payload.Subject = null;
      payload.Placeholders = extractPlaceholders(bodyHtml);
    } else {
      payload.Subject = null;
      payload.Placeholders = extractPlaceholders(bodyHtml);
    }

    if (!isEditMode) {
      if (!currentUser?.id) {
        showSnackbar("Missing creator information", theme, "error");
        return;
      }
      payload.CreatedBy = currentUser.id;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await api.put(
          `/communicationTemplates/${overlayData?.template?.id}`,
          payload,
        );
        showSnackbar("Template updated", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "CommunicationTemplate",
            recordId: overlayData?.template?.id,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else {
        const response = await api.post("/communicationTemplates", payload);
        showSnackbar("Template created", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "CommunicationTemplate",
            recordId: response.data.id,
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }

      overlayData?.onSaved?.();
      closeOverlay();
    } catch (err) {
      console.error("Error saving template", err);
      handleErrorDialog(
        err.response?.data?.error || "Error saving communication template",
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete the template (edit mode only). Removes the template together with
  // its auto-select rules, then refreshes the Settings list.
  const handleDelete = () => {
    if (!isEditMode || !overlayData?.template?.id) return;

    openDialog({
      theme,
      title: "Delete Communication Template",
      message: `Are you sure you want to delete "${name}"? This will also remove its auto-select rules.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(
            `/communicationTemplates/${overlayData.template.id}`,
          );
          closeDialog();
          showSnackbar("Template deleted", theme, "success");

          // Emit socket event for real-time updates
          if (socket) {
            socket.emit("settings:saved", {
              settingsType: "CommunicationTemplate",
              recordId: overlayData.template.id,
              changeAction: "Deleted",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          releaseLock();
          overlayData?.onSaved?.();
          closeOverlay();
        } catch (err) {
          console.error("Error deleting template:", err);
          closeDialog();
          showSnackbar(
            err?.response?.data?.error || "Error deleting template",
            theme,
            "error",
          );
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isEditMode &&
          (isMobile ? (
            <Tooltip title="Delete" placement="top">
              <IconButton
                color="error"
                onClick={handleDelete}
                disabled={saving || isLocked}
                sx={{
                  width: "40px",
                  height: "40px",
                  border: `2px solid ${theme.palette.error.main}`,
                  borderRadius: "12px",
                }}
              >
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              color="error"
              variant="outlined"
              size="large"
              endIcon={<DeleteOutlineRoundedIcon />}
              onClick={handleDelete}
              disabled={saving || isLocked}
              sx={{ borderRadius: "12px" }}
            >
              Delete
            </Button>
          ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            endIcon={<DisabledByDefaultRoundedIcon />}
            onClick={closeOverlay}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        <Tooltip
          title={isMobile && saving ? "Saving changes..." : ""}
          placement="top"
        >
          <span>
            {isMobile ? (
              <IconButton
                color="primary"
                onClick={handleSave}
                disabled={saving || isLocked || hasDuplicateRules}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            ) : (
              <Button
                loading={saving}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={handleSave}
                disabled={saving || isLocked || hasDuplicateRules}
                sx={{ borderRadius: "12px" }}
              >
                Save
              </Button>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Overlay
        id="communication-template-new-edit"
        open={true}
        onClose={closeOverlay}
        title={isEditMode ? "Edit Communication Template" : "New Communication Template"}
        footer={footerActions}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Template Details
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Channel"
                required
                select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
              >
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Type"
                required
                select
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="Template">Template</MenuItem>
                <MenuItem value="Modifier">Modifier</MenuItem>
              </TextField>
            </Grid>

            {type === "Template" && channel === "Email" && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subject"
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onFocus={() => setLastFocused("subject")}
                  inputRef={subjectRef}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            {type === "Template" && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold">
                      Auto-Select Rules
                    </Typography>
                    <Tooltip
                      title="When a service matches any of these rules, this template is preselected in Send Details. Client rules take priority over type rules, which take priority over name rules. The user can always change the template manually from the dropdown."
                      placement="top"
                    >
                      <IconButton size="small" sx={{ color: "text.secondary" }}>
                        <HelpOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddRoundedIcon />}
                    onClick={addMatchRule}
                    sx={{ borderRadius: "8px" }}
                  >
                    Add Rule
                  </Button>
                </Box>

                {matchRules.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No rules yet — add one to auto-select this template.
                  </Typography>
                )}

                {matchRules.map((rule, index) => {
                  const ruleFieldDef = RULE_FIELD_OPTIONS.find(
                    (o) => o.key === rule.Field,
                  );
                  // List-backed fields always match exactly; the Match Type
                  // selector only applies to typed fields.
                  const showMatchType = ruleFieldDef
                    ? !ruleFieldDef.hasList
                    : true;

                  return (
                    <Box
                      key={`rule-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      {/* Left side: the text fields take all available space */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Grid container spacing={1.5}>
                          <Grid size={{ xs: 12, md: showMatchType ? 3 : 4 }}>
                            <TextField
                              fullWidth
                              size="small"
                              select
                              label="Field"
                              value={rule.Field}
                              onChange={(e) => {
                                const nextField = e.target.value;
                                const nextFieldDef = RULE_FIELD_OPTIONS.find(
                                  (o) => o.key === nextField,
                                );
                                updateMatchRule(index, "Field", nextField);
                                // Changing the argument resets its value.
                                updateMatchRule(index, "Value", "");
                                // List-backed fields force exact matching.
                                if (nextFieldDef?.hasList) {
                                  updateMatchRule(index, "MatchType", "exact");
                                }
                              }}
                            >
                              {RULE_FIELD_OPTIONS.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          {showMatchType && (
                            <Grid size={{ xs: 12, md: 2 }}>
                              <TextField
                                fullWidth
                                size="small"
                                select
                                label="Match Type"
                                value={rule.MatchType}
                                onChange={(e) =>
                                  updateMatchRule(
                                    index,
                                    "MatchType",
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="exact">Exact</MenuItem>
                                <MenuItem value="contains">Contains</MenuItem>
                              </TextField>
                            </Grid>
                          )}
                          <Grid size={{ xs: 12, md: showMatchType ? 7 : 8 }}>
                            {renderRuleValueInput(
                              rule,
                              index,
                              duplicateRuleIndexes.has(index),
                            )}
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Far right: the X button wraps to its own width */}
                      <Box sx={{ flexShrink: 0 }}>
                        <IconButton
                          onClick={() => removeMatchRule(index)}
                          size="small"
                        >
                          <ClearRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Body
                    </Typography>

                    {/* Formatting Toolbar */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Tooltip title="Bold">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("bold")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatBoldRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Italic">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("italic")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatItalicRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Underline">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("underline")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatUnderlinedRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                    <Tooltip title="Bullet List">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("insertUnorderedList")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatListBulletedRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Numbered List">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("insertOrderedList")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatListNumberedRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                    <Tooltip title="Indent Right">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("indent")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatIndentIncreaseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Indent Left">
                      <IconButton
                        size="small"
                        onClick={() => applyFormat("outdent")}
                        sx={{ borderRadius: "8px" }}
                      >
                        <FormatIndentDecreaseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" sx={{ my: 0.5 }} />
                    <Tooltip title="Insert Hyperlink">
                      <IconButton
                        size="small"
                        onClick={handleOpenLinkDialog}
                        sx={{ borderRadius: "8px" }}
                      >
                        <LinkRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    </Box>
                  </Box>

                  <Box
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleBodyInput}
                    onFocus={() => setLastFocused("body")}
                    sx={{
                      height: 340,
                      maxHeight: 340,
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      bgcolor: theme.palette.background.default,
                      outline: "none",
                      "&:focus": {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${theme.palette.primary.light}33`,
                      },
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      "& ul, & ol": {
                        paddingLeft: "1.5rem",
                      },
                      "& li": {
                        marginBottom: "0.25rem",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Placeholders
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      p: 1.5,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      bgcolor: theme.palette.background.default,
                      height: 340,
                      maxHeight: 340,
                      overflowY: "auto",
                    }}
                  >
                    {[
                      {
                        group: "Client",
                        items: [
                          { label: "Name", placeholder: "{ClientName}" },
                          { label: "Address", placeholder: "{ClientAddress}" },
                        ],
                      },
                      {
                        group: "Linguist",
                        items: [
                          { label: "Name", placeholder: "{LinguistName}" },
                          { label: "Number", placeholder: "{LinguistNumber}" },
                          {
                            label: "Address",
                            placeholder: "{LinguistAddress}",
                          },
                        ],
                      },
                      {
                        group: "Assignment",
                        items: [
                          {
                            label: "Name",
                            placeholder: "{AssignmentName}",
                          },
                          {
                            label: "Language",
                            placeholder: "{AssignmentLanguage}",
                          },
                          {
                            label: "Type",
                            placeholder: "{AssignmentType}",
                          },
                          {
                            label: "Start Time",
                            placeholder: "{AssignmentStartTime}",
                          },
                          {
                            label: "End Time",
                            placeholder: "{AssignmentEndTime}",
                          },
                          {
                            label: "Duration",
                            placeholder: "{AssignmentDuration}",
                          },
                          { label: "Date", placeholder: "{AssignmentDate}" },
                          {
                            label: "Details",
                            placeholder: "{AssignmentDetails}",
                          },
                          {
                            label: "Address/Link",
                            placeholder: "{AssignmentAddressLink}",
                          },
                          {
                            label: "Requestor",
                            placeholder: "{AssignmentRequestor}",
                          },
                          {
                            label: "Contact Person",
                            placeholder: "{AssignmentContactPerson}",
                          },
                          {
                            label: "Contact Number",
                            placeholder: "{AssignmentContactPersonNumber}",
                          },
                          {
                            label: "Prep Time",
                            placeholder: "{AssignmentPrepTime}",
                          },
                          {
                            label: "Number of Assignments",
                            placeholder: "{NumberOfAssignments}",
                          },
                          {
                            label: "Assignments List",
                            placeholder: "{AssignmentsList}",
                          },
                        ],
                      },
                    ].map((group) => (
                      <Box
                        key={group.group}
                        sx={{
                          border: `1px solid ${theme.palette.primary.border2}`,
                          borderRadius: "10px",
                          p: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ mb: 0.5 }}
                        >
                          {group.group}
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {group.items.map((item) => (
                            <Button
                              key={item.placeholder}
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                insertPlaceholder(item.placeholder)
                              }
                              sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                padding: "2px 8px",
                                minWidth: 0,
                              }}
                            >
                              {item.label}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Grid>

          </Grid>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default CommunicationTemplateNewEdit;
